import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ContentBox,
  ContentPage,
  DatePicker,
  DrawerFilterPanel,
  DropdownAction,
  FullTable,
  NoResults,
  TableFilters,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import useListFilters from "@hooks/useListFilters";
import { downloadTaxInvoice } from "@helpers/taxInvoiceFile";
import { formatBRL, formatCompetencia, formatDate, usePageHeader } from "@components/ow";
import {
  AwaitingInvoiceNotices,
  INVOICE_STATUS_DATA,
  InvoiceDrawer,
  InvoiceFileLink,
  InvoiceStatusCell,
  invoiceCompetenceOf,
  useAwaitingInvoices,
  useInvoiceDrawer,
} from "../../Common/TaxInvoices";

// Notas fiscais do prestador (modelo Lista do DS OneWork): o aviso de cada pagamento
// que espera a nota, com o envio, e o histórico das notas enviadas. Aprovar, recusar e
// pagar são da empresa (/c/nfes).

// Página da API por requisição: uma das opções do seletor da FullTable (10, 25, 50, 100)
const PAGE_SIZE = 25;

const INITIAL_FILTERS = { status: INVOICE_STATUS_DATA[0] };

const BREADCRUMB = [{ page: "Notas fiscais", route: "/e/nfes" }];

const PAYMENTS_HREF = "/e/payment-schedules";

const EMPTY = { totalRegisters: 0, list: [], totalPages: 1 };

export default function NFesEmployee() {
  const navigate = useNavigate();
  const taxInvoicesService = useService("taxInvoices");
  const paymentScheduleService = useService("payment-schedules");

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [paginationData, setPaginationData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  // Pagamentos pendentes que esperam a nota (os avisos do topo)
  const awaitingInvoices = useAwaitingInvoices();
  // Só a resposta da última requisição vale (filtros seguidos podem voltar fora de ordem)
  const lastRequest = useRef(0);

  // O prestador vê o pagamento das próprias notas; sem scope=company o back só devolve os dele
  const drawer = useInvoiceDrawer({ enabled: true, fetchSchedule: fetchScheduleOf });

  const {
    storeFilters,
    filters,
    filter,
    openFilter,
    setOpenFilter,
    handleSetFilters,
    handleConfirmFilter,
    handleClearFilters,
    hasFilter,
  } = useListFilters(INITIAL_FILTERS, setPagination);

  usePageHeader({ title: "Notas fiscais", breadcrumb: BREADCRUMB });

  const statusValue = filters.status?.value || "";
  const searchTerm = (filters.search || "").trim();
  const competence = filters.competence || null;
  const competenceKey = competence ? `${competence.getFullYear()}-${competence.getMonth() + 1}` : "";

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, statusValue, searchTerm, competenceKey]);

  function buildQuery() {
    // Sem scope=company o back devolve só as NFs de quem chama
    const params = [`page=${pagination.pageIndex + 1}`, `limit=${pagination.pageSize}`];
    if (statusValue) params.push(`status=${statusValue}`);
    if (competence) params.push(`month=${competence.getMonth() + 1}`, `year=${competence.getFullYear()}`);
    if (searchTerm) params.push(`filename=${encodeURIComponent(searchTerm)}`);
    return params.join("&");
  }

  async function fetchInvoices() {
    const request = ++lastRequest.current;
    setLoading(true);
    try {
      const result = await taxInvoicesService.list(buildQuery());
      if (request !== lastRequest.current) return;
      if (result && result.data && result.data.list) {
        setPaginationData(result.data);
      } else {
        throw new Error(typeof result === "string" ? result : "Erro ao carregar notas fiscais");
      }
    } catch (error) {
      if (request !== lastRequest.current) return;
      console.error(error);
      toast.error(error?.response?.data?.msg || error?.message || "Erro ao carregar notas fiscais");
      setPaginationData(EMPTY);
    }
    setLoading(false);
  }

  async function fetchScheduleOf(nf) {
    const result = await paymentScheduleService.list(`tax_invoice_id=${nf.id}&limit=1`);
    return result?.data?.list?.[0] || null;
  }

  // Limpa busca, competência e também o status (o "Limpar filtros" do vazio)
  function clearAll() {
    handleClearFilters();
    storeFilters.setFilters({ status: INVOICE_STATUS_DATA[0] });
  }

  const handleDownload = (nf) => downloadTaxInvoice(taxInvoicesService, nf);

  const openPayment = (id) => navigate(`${PAYMENTS_HREF}/${id}`);

  const columns = [
    {
      id: "competence",
      header: "Competência",
      size: 120,
      cell: ({ row }) => formatCompetencia(invoiceCompetenceOf(row.original)),
    },
    {
      id: "file",
      header: "Arquivo",
      size: 260,
      cell: ({ row }) => <InvoiceFileLink invoice={row.original} onDownload={() => handleDownload(row.original)} />,
    },
    { id: "value", header: "Valor", size: 130, cell: ({ row }) => formatBRL(row.original.value) },
    {
      id: "status",
      header: "Status",
      size: 260,
      cell: ({ row }) => <InvoiceStatusCell invoice={row.original} showReason />,
    },
    { id: "created_at", header: "Enviada em", size: 120, cell: ({ row }) => formatDate(row.original.created_at) },
    {
      id: "actions",
      // O "Ações" da lib tem 70px; abaixo disso a seta sai da célula
      size: 80,
      minSize: 80,
      maxSize: 80,
      align: "right",
      cell: ({ row }) => {
        const nf = row.original;
        return (
          <DropdownAction
            options={[
              { label: "Ver dados", action: () => drawer.openDrawer(nf) },
              { label: "Baixar nota", action: () => handleDownload(nf) },
            ]}
          />
        );
      },
    },
  ];

  const filtering = !!statusValue || !!hasFilter || !!searchTerm;
  const noResults = filtering ? (
    <NoResults notResultText="Nenhuma nota fiscal encontrada" includeText="Limpar filtros" includeAction={clearAll} />
  ) : (
    <NoResults notResultText="Nenhuma nota fiscal por aqui" />
  );

  return (
    <ContentPage>
      {awaitingInvoices.awaiting.length > 0 ? (
        <ContentBox>
          <AwaitingInvoiceNotices
            awaiting={awaitingInvoices.awaiting}
            onSent={() => {
              awaitingInvoices.reload();
              fetchInvoices();
            }}
          />
        </ContentBox>
      ) : null}

      <ContentBox>
        <TableFilters
          storeFilters={storeFilters}
          searchTerm="search"
          searchPlaceholder="Buscar pelo nome do arquivo"
          statusData={INVOICE_STATUS_DATA}
          filter={filter}
          handleSetFilters={handleSetFilters}
          handleClearFilters={handleClearFilters}
          setStatePagination={setPagination}
          openFilter={() => setOpenFilter((prev) => !prev)}
          disableExport
          hasFilter={hasFilter}
        />

        <FullTable
          data={paginationData.list}
          columns={columns}
          rowCount={paginationData.totalRegisters}
          pagination={pagination}
          setPagination={setPagination}
          getRowId={(row) => String(row.id)}
          isLoading={loading}
          onRowClick={drawer.openDrawer}
          noResults={noResults}
        />
      </ContentBox>

      <DrawerFilterPanel
        $showDrawer={openFilter}
        $setShowDrawer={() => setOpenFilter((prev) => !prev)}
        $confirmFilter={handleConfirmFilter}
        $clearFilters={handleClearFilters}
        $hasFilter={hasFilter}
      >
        <DatePicker
          $idForm="employee-nfes-competence"
          $label="Competência"
          $variant="default"
          $isCompetence
          $isClearable
          $date={filter.competence || null}
          $setDate={(date) => handleSetFilters(null, "competence", date || null)}
        />
      </DrawerFilterPanel>

      <InvoiceDrawer drawer={drawer} onDownload={handleDownload} onOpenPayment={openPayment} />
    </ContentPage>
  );
}
